import { Component, Vue } from 'vue-property-decorator';
import { Inject } from 'vue-di-container';

import WithRender from './contract-details.component.html';
import OtpComponent from '@/components/OTP/OtpComponent.vue';
import { OTPRefrenceDataModel } from '@/models/OTPRefrenceDataModel';
import { ContractDetails } from '@/models/contractDetailsModel';
import { ContractPartnerModel } from '@/models/contractPartnerModel';
import UserModel from '@/models/user';
import ContractService from '@/services/contract.service';
import ExternalService from '@/services/external';
import { Toaster } from '@/services/toast';
import UserService from '@/services/user';
import { moduleProxy } from '@/store';
import { EventBus, Events } from '@/event-bus';
import SettleContractComponent from '@/components/dialog/settle-contract.component.vue';
import { ContractAction as Actions } from '@/models/actions.models';
import ContractBodyComponent from '@/components/contract/contract-body.component.vue';
import ContractActionsComponent from '@/components/contract/contract-actions.component.vue';

@WithRender
@Component({
  components: { ContractBodyComponent, ContractActionsComponent, OtpComponent },
  diProvide: [
    UserService,
    ContractService,
    ExternalService,
  ],
})
export default class ContractDetailsComponent extends Vue {
  @Inject(UserService) public userService!: UserService;
  @Inject(ContractService) public contractService!: ContractService;
  @Inject(ExternalService) public externalService!: ExternalService;

  contractId = 0;
  contractDetails: ContractDetails = {} as ContractDetails;
  currentUser = {} as UserModel;
  currentPartner = {} as ContractPartnerModel;

  /** Holds the selected action. */
  action?: Actions;

  /** Holds the contract close reason. */
  settleContractReason?: number;

  created() {
    // Subscribe to an event.
    EventBus.$on(Events.Contract.ConfirmSettleContractEvent, this.onConfirmSettleContract);
  }

  mounted() {
    this.getContractDetails(parseInt(this.$route.params.id.toString(), 10));
  }

  beforeDestroy() {
    // Unsubscribe from an event.
    EventBus.$off(Events.Contract.ConfirmSettleContractEvent, this.onConfirmSettleContract);
  }

  getContractDetails(id: number) {
    if (!id) {
      Toaster.error({ message: 'خطأ في رقم الوثيقة' });
      this.$router.push({ name: 'userContracts' });
    }

    this.contractService.getContractDetails(id).then((response) => {
      this.contractId = response.model.id;
      this.contractDetails = response.model;
      this.getCurrentUser();
    }).catch(() => {
      this.$router.push({ name: 'userContracts' });
    });
  }

  getCurrentUser() {
    this.userService.getUser().then((res) => {
      this.currentUser = res;
      const currentPartner = this.contractDetails.partners.find((partner) => partner.socialId === this.currentUser.username);
      if (currentPartner) {
        this.currentPartner = currentPartner;
      }
    });
  }

  onDownloadContract(code: number) {
    this.contractService.getContractAsPDF(code).then((res) => {
      const linkSource = `data:application/pdf;base64,${res.model}`;
      const downloadLink = document.createElement('a');
      const fileName = `${code}.pdf`;
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    });
  }

  /**
   * Handles the various view actions.
   * @param action The action to be performed.
   * @returns void
   */
  onHandleAction(action: Actions): void {
    this.action = action;

    switch (this.action) {
      case 'ApproveRequest':
      case 'RejectRequest':
        this.initApproveRejectRequest();
        break;
      case 'SettleContract':
        this.initSettleContract();
        break;
      case 'DownloadContract':
        if (this.contractDetails.contractCode) {
          this.onDownloadContract(this.contractDetails.contractCode);
        }
        break;
      default:
        throw new Error('Invalid action.');
    }
  }

  /**
   * An event handler for the verified OTP.
   * @param otpData The OTP data.
   */
  async onOtpVerified(otpData: OTPRefrenceDataModel): Promise<void> {
    if (!otpData) return;

    switch (this.action) {
      case 'ApproveRequest':
      case 'RejectRequest':
        await this.submitApproveReject(otpData);
        break;
      case 'SettleContract':
        await this.settleContract(otpData);
        break;
      default:
        throw new Error('Invalid action.');
    }
  }

  /**
   * Initiates the approval/rejection of the request.
   */
  private async initApproveRejectRequest(): Promise<void> {
    await (this.$refs.otp as any).sendOtp();
  }

  /**
   * Sends a request to approve/reject the request.
   * @param otpData The verified OTP data.
   * @returns void
   */
  private async submitApproveReject(otpData: OTPRefrenceDataModel): Promise<void> {
    const partner = this.contractDetails.partners.find((x) => x.socialId === this.currentUser.username);

    if (!partner?.id) return;

    const result = await this.contractService.approveRejectContract({
      contractId: this.contractDetails.id,
      partnerId: partner.id,
      isApprove: this.action === 'ApproveRequest',
      oTPRefrenceDataDTO: otpData,
    });

    if (result.statusCode === 200) {
      this.$router.push({ name: 'userContracts' }).then(() => {
        moduleProxy.mainAlert.show({ template: `<div class="font-weight-bold" style="font-size: 1.5rem;">${result.message}</div>` });
      });
    }
  }

  /**
   * Initiates the settle contract process.
   */
  private initSettleContract(): void {
    // Publish an event.
    EventBus.$emit(Events.Dialog.OpenDialogEvent, SettleContractComponent);
  }

  /**
   * An event handler to confirm settle contract process.
   * @param reason The close reason.
   * @returns void
   */
  private async onConfirmSettleContract(reason: number): Promise<void> {
    if (!reason) return;

    this.settleContractReason = reason;
    await (this.$refs.otp as any).sendOtp();
  }

  /**
   * Sends a settle contract request.
   * @param otpData The verified OTP data.
   * @returns void
   */
  private async settleContract(otpData: OTPRefrenceDataModel): Promise<void> {
    if (!this.contractDetails.contractCode || !this.settleContractReason) return;

    const result = await this.contractService.settleContract({
      contractCode: this.contractDetails.contractCode,
      reasonId: this.settleContractReason,
      oTPRefrenceDataDTO: otpData,
    });

    if (result.statusCode === 200) {
      this.$router.push({ name: 'userContracts' }).then(() => {
        moduleProxy.mainAlert.show({ template: `<div class="font-weight-bold" style="font-size: 1.5rem;">تم إرسال طلب تسوية العقد إلى الأطراف للمصادقة بنجاح</div><div class="text-dark text-sm">رقم الطلب: ${result.model.requestCode}</div>` });
      });
    }
  }
}
